// Shared builders for the trunk/geom/appr ONNX decoder nodes — ONE DecoderSession
// config for every construction site. gpuBufferIo is read from the NodeContext at init.
//
// Every session is born in decoderBuild.createResolvedDecoder, so all three get
// the ORT env assert and the fp16-preferred → fp32-fallback resolve for free,
// multi-region branches included.
//
// DO NOT enable ORT graph capture on these sessions. `enableGraphCapture` records
// the command list once and replays it, and replay only re-reads inputs bound by
// the graph's LEADING op. A region-factored geom/appr binds `code` on its leading
// ConvTranspose, so `zpack` — the per-group rig latent the FiLM slices — would be
// frozen at whatever the RECORDING frame held: the face would modulate with a
// stale expression, every frame, with no error raised. (`src/lib/ortBridge.js`
// does enable capture, deliberately, for the small fixed-shape plugin MLPs; it is
// reached only through pluginLoader and never builds a decoder here.) Locked by
// tests/unit/decoderZpack.test.mjs.
//
// NOTE for a future fused-decoder bundle (trunk_combined / geom_combined /
// appr_combined ship today, unreferenced): collapsing head+body into one dispatch
// is a CONFIG change here, not a rewrite — declare two output pairs on the same
// DecoderSession (`outputs: [{port:'geom_uv@head'},{port:'geom_uv@body'}]`) and
// give OGSRenderer those as two sources. Do not delete those assets.

import { OGSDecoder } from "./OGSDecoder.ts";
import { createResolvedDecoder, type DecoderKind } from "../decoderBuild.ts";
import type { DecoderSession, DecoderSessionOptions } from "../DecoderSession";
import type { NodeContext } from "../types";

export interface DecoderFactoryOptions {
  /** fp32 model bytes (or URL), eager. Either this or getFp32 is required. */
  bytes?: Uint8Array | string;
  /** Lazily-resolved fp32 source, so an fp16-preferred bundle needn't hold fp32. */
  getFp32?: () => Promise<Uint8Array | string>;
  /** fp16-internal variant (fp32 IO). Absent → fp32-only, no downgrade report. */
  fp16Bytes?: Uint8Array | string;
  /** Caller pre-gates on EP + ogsGpuFlags + the Avatar node's precision pick. */
  want16?: boolean;
  providers: string[];
  id?: string;
  /** Human label for the build log, e.g. 'head.geom'. */
  label?: string;
  /** Extra session options (profiler); merged UNDER preferredOutputLocation. */
  extra?: Record<string, unknown>;
  onDowngrade?: (kind: DecoderKind, reason: string) => void;
  /** Reports the precision the session actually built at. */
  onResolved?: (usedFp16: boolean) => void;
}

// One builder for all three: resolve fp16/fp32, build, report.
function buildSession(
  kind: DecoderKind,
  opts: DecoderFactoryOptions,
  ctx: NodeContext,
  spec: DecoderSessionOptions,
  outputLocations: Record<string, string> | null,
): Promise<{ session: DecoderSession; usedFp16: boolean }> {
  const getFp32 = opts.getFp32
    ?? (opts.bytes
      ? () => Promise.resolve(opts.bytes as Uint8Array | string)
      // Loud, never a stub session (CLAUDE.md UX rules).
      : () => Promise.reject(new Error(`decoderFactories: no fp32 source for '${opts.label ?? opts.id ?? kind}'`)));
  return createResolvedDecoder({
    kind,
    label: opts.label,
    want16: !!opts.want16 && !!opts.fp16Bytes,
    fp16Src: opts.fp16Bytes,
    onWebgpu: opts.providers[0] === "webgpu",
    getFp32,
    ort: ctx.ort as never,
    providers: opts.providers,
    spec,
    // profiler options spread FIRST so enableProfiling can't override the
    // location hints (later key wins).
    extra: outputLocations ? { ...(opts.extra ?? {}), preferredOutputLocation: outputLocations } : (opts.extra ?? {}),
    onDowngrade: opts.onDowngrade,
  }).then((res) => {
    opts.onResolved?.(res.usedFp16);
    return res;
  });
}

/** The trunk's output location hints. `zpack` rides with `code` or neither does.
 *
 *  Exported so the contract is unit-testable without building an ORT session —
 *  a regression here is invisible in every functional test, because a CPU zpack
 *  produces the RIGHT pixels, just far too slowly. */
export function trunkOutputLocations(gpuIo: boolean): Record<string, string> | null {
  return gpuIo ? { code: "gpu-buffer", zpack: "gpu-buffer" } : null;
}

/** rig_params (or `head_disp`) → `code` latent. The multi-region trunk: the geom/appr decoders of
 *  a branch consume its output instead of the rig directly (the single-region
 *  bundle fuses this into geom).
 *
 *  REGION-FACTORED (V8+) bundles emit a SECOND output, `zpack`: the packed
 *  per-group rig latent, which geom/appr slice for their baked mask-gated FiLM.
 *  It is declared optional, so a pre-V8 trunk (no such output) is unaffected and
 *  the mode is detected from the graph that loaded rather than from a flag.
 *
 *  `zpack` IS gpu-buffer-hinted, and the size argument for leaving it on the CPU
 *  is a trap: at ~256 floats the transfer really is noise, but the COST IS NOT
 *  THE TRANSFER — it is the SYNC. An output ORT must hand back as a CPU tensor
 *  has to be downloaded, and the download cannot start until the GPU has
 *  finished the run, so a CPU zpack turns every trunk into a queue drain. It
 *  fires once per BRANCH (head/body/teeth = 3× per pipeline), which is why the
 *  damage shows up as branches that stop overlapping rather than as one slow
 *  decoder. Measured on isaac-v8 (locally, GPU clocks locked, A/B/A over four
 *  runs): 5.8/5.7 pipelines·s⁻¹ with zpack on the CPU against 9.9/9.6 with it
 *  gpu-resident — a 1.7× loss, which put a V8 bundle at half the throughput of
 *  the V6 that has no zpack output at all. A single-content (composite) branch
 *  declares no zpack and is byte-identical either way. */
export function makeTrunkDecoder(opts: DecoderFactoryOptions): OGSDecoder {
  const id = opts.id ?? "trunk";
  return new OGSDecoder({
    id,
    inputs: ["rig_params", "head_disp"],
    outputs: ["code", "zpack"],
    dirtyOn: ["rig"],
    createSession: (ctx) => {
      const gpuIo = ctx.gpuBufferIo;
      return buildSession(
        "geom",
        { ...opts, want16: false, fp16Bytes: undefined, onDowngrade: undefined },
        ctx,
        {
          // BOTH inputs are optional so the GRAPH decides which conditioning
          // this trunk takes -- the same rule `zpack` follows below, for the
          // same reason: a flag and an asset can disagree, a declared input
          // cannot. A rig trunk declares `rig_params`; a mesh-conditioned head
          // (TexAvatars --head_mesh_cond) declares `head_disp` and no rig at
          // all. DecoderSession REQUIRES a tensor for whichever the loaded
          // graph declares, so "optional" never means "silently skipped": a
          // displacement trunk run without its field throws instead of
          // modulating with whatever ORT defaults to.
          inputs: [
            { port: "rig_params", ortName: "rig_params", optional: true },
            { port: "head_disp", ortName: "head_disp", optional: true },
          ],
          outputs: [
            { port: "code", ortName: "code", cache: true, gpuBuffer: gpuIo },
            { port: "zpack", ortName: "zpack", cache: true, optional: true, gpuBuffer: gpuIo },
          ],
        },
        trunkOutputLocations(gpuIo),
      ).then((r) => r.session);
    },
  });
}

export interface GeomDecoderOptions extends DecoderFactoryOptions {
  /** Which ORT input the geom decoder takes: 'rig_params' for the FUSED
   *  single-region graph (which also emits the `code` latent), 'code' for a
   *  multi-region branch whose separate trunk already produced it, or 'disp_map'
   *  for a --head_local_disp head (myra_v16): the per-texel displacement UV map,
   *  (1,3,H,W). That graph has its code BAKED IN and emits geom_uv only. */
  input?: "rig_params" | "code" | "disp_map";
}

// rig_params → geom_uv (+ cached `code` latent, reused while only the camera moves),
// code → geom_uv on the trunk-split multi-region graph, or disp_map → geom_uv on
// the local_disp head. code + geom_uv stay on gpu-buffer on the shared-device path.
export function makeGeomDecoder(opts: GeomDecoderOptions): OGSDecoder {
  const id = opts.id ?? "geom";
  const input = opts.input ?? "rig_params";
  const fused = input === "rig_params";
  // A displacement-fed geom takes the field alone: no trunk latent, no zpack. It
  // is dirty on the RIG like the fused one — a new head pose is a new field.
  const disp = input === "disp_map";
  return new OGSDecoder({
    id,
    inputs: fused || disp ? [input] : [input, "zpack"],
    outputs: fused ? ["geom_uv", "code"] : ["geom_uv"],
    // A trunk-fed geom is dirty via its upstream edge, not the rig epoch.
    dirtyOn: fused || disp ? ["rig"] : [],
    want16: !!opts.want16,
    createSessionFor: (ctx, want16) => {
      const gpuIo = ctx.gpuBufferIo;
      return buildSession(
        "geom",
        { ...opts, want16 },
        ctx,
        {
          inputs: [
            { port: input, ortName: input },
            // Region-factored branches only; see makeTrunkDecoder. Per-SESSION,
            // because one V9 bundle mixes FiLM branches (head) with composite
            // ones (body) that take `code` alone.
            ...(fused || disp ? [] : [{ port: "zpack", ortName: "zpack", optional: true }]),
          ],
          outputs: [
            ...(fused ? [{ port: "code", ortName: "code", cache: true, gpuBuffer: gpuIo }] : []),
            { port: "geom_uv", ortName: "geom_uv", gpuBuffer: gpuIo },
          ],
        },
        gpuIo ? (fused ? { code: "gpu-buffer", geom_uv: "gpu-buffer" } : { geom_uv: "gpu-buffer" }) : null,
      );
    },
  });
}

export interface ApprDecoderOptions extends DecoderFactoryOptions {
  /** A --head_local_disp head's appr: `plucker` (+ `disp_map` when the graph
   *  declares it), no `code` (baked into the graph) and no `zpack`. Dirty on the
   *  rig as well as the camera. */
  dispMap?: boolean;
  /** A PBR branch's appr emits a MATERIAL, which no camera can change. */
  viewIndependent?: boolean;
}

// code + plücker → color_uv (view-dependent), or plücker + disp_map → color_uv on
// the local_disp head. serialize:true = the appr back-pressure queue.
export function makeApprDecoder(opts: ApprDecoderOptions): OGSDecoder {
  const id = opts.id ?? "appr";
  const disp = !!opts.dispMap;
  // A PBR appr is view-independent BY EXPORT (its plücker is baked to zeros), so
  // an orbit must not re-run it — on clothes that graph is 86 MB.
  const viewDep = !opts.viewIndependent;
  return new OGSDecoder({
    id,
    inputs: disp ? ["plucker", "disp_map"] : ["code", "plucker", "zpack"],
    outputs: ["color_uv"],
    dirtyOn: disp ? (viewDep ? ["camera", "rig"] : ["rig"]) : (viewDep ? ["camera"] : []),
    want16: !!opts.want16,
    createSessionFor: (ctx, want16) => {
      const gpuIo = ctx.gpuBufferIo;
      return buildSession(
        "appr",
        { ...opts, want16 },
        ctx,
        {
          inputs: disp ? [
            // THE GRAPH DECIDES, for plücker as much as for zpack/disp_map. A PBR
            // appr declares none — the export bakes a zero plücker in, because a
            // material is not a function of the view — and feeding one is an ORT
            // "invalid input 'plucker'". docs/OGS_PBR_PARITY.md §5b.
            { port: "plucker", ortName: "plucker", optional: true },
            // The GRAPH decides, as with zpack: a --head_appr_disp_fuse 0 run exports
            // appr with `plucker` alone (colour is the base; the motion residual had
            // degenerated into a constant gate), a fused run declares `disp_map`.
            // DecoderSession still REQUIRES the tensor for a graph that declares it.
            { port: "disp_map", ortName: "disp_map", optional: true },
          ] : [
            { port: "code", ortName: "code" },
            { port: "plucker", ortName: "plucker", optional: true },
            // Region-factored branches only; see makeTrunkDecoder.
            { port: "zpack", ortName: "zpack", optional: true },
          ],
          outputs: [{ port: "color_uv", ortName: "color_uv", gpuBuffer: gpuIo }],
          serialize: true,
        },
        gpuIo ? { color_uv: "gpu-buffer" } : null,
      );
    },
  });
}
